import {
  List,
  ListItem,
  ListItemText,
  Switch,
  Typography,
  Box,
  Divider,
} from '@mui/material'

export interface TeamPermissions {
  readCertificate: boolean
  signDocument: boolean
  readDocument: boolean
  downloadDocument: boolean
  deleteDocument: boolean
  updateDocument: boolean
  readSession: boolean
  readSystemSettings: boolean
  updateSystemSettings: boolean
}

export const DEFAULT_PERMISSIONS: TeamPermissions = {
  readCertificate: true,
  signDocument: true,
  readDocument: true,
  downloadDocument: true,
  deleteDocument: true,
  updateDocument: true,
  readSession: true,
  readSystemSettings: false,
  updateSystemSettings: false,
}

export const PERMISSION_LABELS: Record<keyof TeamPermissions, string> = {
  readCertificate: 'Sertifika listesini görüntüleme',
  signDocument: 'Belge imzalama (UYAP girişi, evrak imzalama)',
  readDocument: 'Belgeleri görüntüleme',
  downloadDocument: 'İmzalı belgeleri indirme',
  deleteDocument: 'Belge silme',
  updateDocument: 'Belge durumunu güncelleme',
  readSession: 'Oturum listesini görüntüleme (UYAP oturumu kullanma)',
  readSystemSettings: 'Takım ayarlarını görüntüleme',
  updateSystemSettings: 'Takım ayarlarını güncelleme',
}

export const PERMISSION_CATEGORIES = {
  certificate: {
    label: 'Sertifika İzinleri',
    keys: ['readCertificate'] as (keyof TeamPermissions)[],
  },
  document: {
    label: 'Belge İzinleri',
    keys: [
      'signDocument',
      'readDocument',
      'downloadDocument',
      'deleteDocument',
      'updateDocument',
    ] as (keyof TeamPermissions)[],
  },
  session: {
    label: 'Oturum İzinleri',
    keys: ['readSession'] as (keyof TeamPermissions)[],
  },
  system: {
    label: 'Sistem Ayarları İzinleri',
    keys: ['readSystemSettings', 'updateSystemSettings'] as (keyof TeamPermissions)[],
  },
}

interface PermissionCheckboxListProps {
  permissions: TeamPermissions
  onChange: (key: keyof TeamPermissions, value: boolean) => void
  disabled?: boolean
  showCategories?: boolean
}

export function PermissionCheckboxList({
  permissions,
  onChange,
  disabled = false,
  showCategories = true,
}: PermissionCheckboxListProps) {
  if (showCategories) {
    return (
      <List sx={{ p: 0 }}>
        {Object.entries(PERMISSION_CATEGORIES).map(
          ([categoryKey, category], categoryIndex) => (
            <Box key={categoryKey}>
              {categoryIndex > 0 && <Divider />}
              <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50' }}>
                <Typography variant="caption" color="text.secondary" fontWeight="medium">
                  {category.label}
                </Typography>
              </Box>
              {category.keys.map((key) => (
                <ListItem
                  key={key}
                  sx={{ py: 0.5 }}
                  secondaryAction={
                    <Switch
                      edge="end"
                      checked={permissions[key]}
                      onChange={(e) => onChange(key, e.target.checked)}
                      disabled={disabled}
                      size="small"
                    />
                  }
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2">{PERMISSION_LABELS[key]}</Typography>
                    }
                  />
                </ListItem>
              ))}
            </Box>
          ),
        )}
      </List>
    )
  }

  // Kategori olmadan düz liste
  return (
    <List sx={{ p: 0 }}>
      {(Object.keys(permissions) as (keyof TeamPermissions)[]).map((key, index) => (
        <Box key={key}>
          {index > 0 && <Divider />}
          <ListItem
            sx={{ py: 0.5 }}
            secondaryAction={
              <Switch
                edge="end"
                checked={permissions[key]}
                onChange={(e) => onChange(key, e.target.checked)}
                disabled={disabled}
                size="small"
              />
            }
          >
            <ListItemText
              primary={
                <Typography variant="body2">{PERMISSION_LABELS[key]}</Typography>
              }
            />
          </ListItem>
        </Box>
      ))}
    </List>
  )
}
